import React, {useState} from 'react';
import {StoreResultResponse} from 'app/components/match/liveResult/types';
import UpdateResultForm, {ResultFormInputs} from 'app/components/match/liveResult/UpdateResultForm';
import UpdateResultHeader from 'app/components/match/liveResult/UpdateResultHeader';
import {AxiosResponse} from 'axios';
import {authorizedAPI} from 'app/services/api';
import {sendEventToGA4} from 'app/helpers/gtagHelpers';
import {wsUpdateMatch} from 'app/routes/match/MatchPageActions';
import {handleResultSuccess} from 'app/actions/softTickerActions';
import {useSoftTickerContext} from 'app/hooks/useSoftTickerContext';
import {useSelector} from 'react-redux';
import {selectCurrentUser} from 'app/hooks/reduxCreateSelectorHooks';
import isAxiosError from 'app/helpers/isAxiosError';
import {ApiErrorResponseWithFields} from 'app/common/errorTypes';
import {UseFormSetError} from 'react-hook-form';

interface UpdateResultProps {
  match: any;
  onClose: () => void;
  onResultChange: (updateFunc: () => void) => void;
}

type StoreResultData = {
  minute: number;
  homeGoals: number;
  awayGoals: number;
};

type ValidatedFormData = {
  homeGoals: number;
  awayGoals: number;
  minute: number;
  final: boolean;
};

const storeResult = (
  matchSlug: string,
  final: boolean,
  data: StoreResultData
): Promise<AxiosResponse<StoreResultResponse>> => {
  return authorizedAPI.post(`/matches/${matchSlug}/results/${final ? 'final' : 'interim'}`, data);
};

const UpdateResult = ({match, onClose, onResultChange}: UpdateResultProps) => {
  const {softTickerData, dispatch} = useSoftTickerContext();
  const {fuPaUser}: any = useSelector(selectCurrentUser);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (formData: ValidatedFormData, setFieldError: UseFormSetError<ResultFormInputs>) => {
    setLoading(true);
    try {
      const response = await storeResult(match.slug, formData.final, {
        minute: formData.minute,
        homeGoals: formData.homeGoals,
        awayGoals: formData.awayGoals,
      });
      setLoading(false);
      sendEventToGA4('soft_ticker_report_live_result', {
        soft_ticker_action: 'save',
        ui_view: 'layer_report_live_result',
        match_slug: match.slug,
      });
      const resultData = response.data;
      dispatch(handleResultSuccess(resultData));
      onResultChange(() =>
        wsUpdateMatch({
          minute: resultData.minute,
          additionalMinute: resultData.additionalMinute,
          homeGoal: resultData.homeGoals,
          awayGoal: resultData.awayGoals,
          flags: ['softticker'],
          lastSofttickerReporter: {firstName: fuPaUser.firstName},
          softtickerEventsReportableUntil: resultData.softtickerEventsReportableUntil,
        })
      );
    } catch (error: unknown) {
      setLoading(false);
      if (isAxiosError<ApiErrorResponseWithFields>(error)) {
        const errorData = error.response?.data?.error;
        if (errorData?.code === 422) {
          // Validation-Error
          const fields = errorData.fields;
          if (fields && Object.keys(fields).length) {
            Object.keys(fields).forEach(field => {
              const input = field as keyof ResultFormInputs;
              setFieldError(input, {
                type: 'custom',
                message: fields[field]?.[0] ?? errorData.translated_message,
              });
            });
            return;
          }
        }
        if (errorData?.translated_message) {
          return setError(errorData.translated_message);
        }
      }
      setError('Es ist ein unbekannter Fehler aufgetreten, versuchen sie es später erneut.');
    }
  };

  return (
    <>
      <UpdateResultHeader match={match} resultData={null} />
      <UpdateResultForm
        match={match}
        data={softTickerData.createResult}
        loading={loading}
        onClose={onClose}
        submissionError={error}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default UpdateResult;
